import React from 'react';
import dayjs from 'dayjs';
import { type IChildAccount } from '../../interfaces/IChildAccount';
import ChildAccountStatus from './ChildAccountStatus';
import ReconfigureAccountButton from '../controls/ReconfigureAccountButton';
import { dateTimeFormat } from '../../utilities/constants';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Button from '../controls/Button';

interface AccountsTableProps {
  accounts: IChildAccount[];
  canUpdate: boolean;
  eventSlug?: string;
  organizationAccount?: string;
}

const AccountsTable: React.FC<AccountsTableProps> = ({ accounts, canUpdate, eventSlug, organizationAccount }) => {
  if (accounts.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 table-fixed">
          <colgroup>
            <col className="w-1/4" />
            <col className="w-1/6" />
            <col className="w-1/4" />
            <col className="w-1/6" />
            {canUpdate && <col className="w-1/6" />}
          </colgroup>
          <thead>
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm text-gray-900">Account ID</th>
              <th className="px-3 py-3.5 text-left text-sm text-gray-900">Status</th>
              <th className="px-3 py-3.5 text-left text-sm text-gray-900">Assigned To</th>
              <th className="px-3 py-3.5 text-left text-sm text-gray-900">Allocated At</th>
              {canUpdate && <th className="px-3 py-3.5 text-left text-sm text-gray-900">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {accounts.map((account, index) => (
              <tr key={index}>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {account.identifier ? (
                    <a
                      href={`https://${
                        organizationAccount ? organizationAccount.split(/[-.]/, 1)[0].toLowerCase() + '-' : ''
                      }${account.identifier.toLowerCase()}.snowflakecomputing.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      title={`Open ${account.identifier} in Snowflake`}
                    >
                      {account.identifier}
                    </a>
                  ) : (
                    <span>Identifier not available for account with slug {account.slug}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <ChildAccountStatus status={account.status} />
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {account.allocated_to ?? 'Unassigned'}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {account.allocated_at ? dayjs(account.allocated_at).format(dateTimeFormat) : 'N/A'}
                </td>
                {canUpdate && (
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                    <div className="flex flex-col gap-1 max-w-max">
                      <ReconfigureAccountButton
                        account={account}
                        eventSlug={eventSlug}
                        intent="primary"
                        className="hover:bg-dataops-primary-dark-blue/90 transition-colors"
                        canUpdate={canUpdate}
                      />
                      <Button
                        url={account.latest_pipeline_url ?? undefined}
                        intent="light"
                        size="small"
                        className="hover:bg-gray-100 transition-colors"
                        disabled={!account.latest_pipeline_url}
                        title={!account.latest_pipeline_url ? 'No pipeline available' : 'View latest pipeline'}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                        <span>View Pipeline</span>
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountsTable;
